import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { BookOpen, Search, Star, X, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { proxyCoverUrl } from "../utils/coverProxy";

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "All");
  const [selectedHomepageTheme, setSelectedHomepageTheme] = useState(searchParams.get("homepageTheme") || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchBooks = useCallback(async (retries = 3) => {
    setLoading(true);
    for (let i = 0; i < retries; i++) {
      try {
        const params = new URLSearchParams();
        if (selectedCategory && selectedCategory !== "All" && !selectedHomepageTheme) {
          params.append("category", selectedCategory);
        }
        if (selectedHomepageTheme) {
          params.append("homepageTheme", selectedHomepageTheme);
        }
        if (searchQuery) {
          params.append("search", searchQuery);
        }
        params.append("limit", "400");

        const response = await axios.get(`${API}/books?${params.toString()}`);
        setBooks(response.data.books);
        setTotal(response.data.total);
        setLoading(false);
        return;
      } catch (error) {
        if (i === retries - 1) {
          console.error("Failed to fetch books:", error);
          setLoading(false);
        } else {
          await new Promise(r => setTimeout(r, 1500));
        }
      }
    }
  }, [selectedCategory, selectedHomepageTheme, searchQuery]);

  const fetchCategories = async (retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await axios.get(`${API}/books/categories`);
        setCategories(response.data);
        return;
      } catch (error) {
        if (i === retries - 1) console.error("Failed to fetch categories:", error);
        else await new Promise(r => setTimeout(r, 1500));
      }
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBooks();
  };

  const clearFilters = () => {
    setSelectedCategory("All");
    setSelectedHomepageTheme("");
    setSearchQuery("");
    setSearchParams({});
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <Helmet>
        <title>Browse Library — 350+ Curated Books | The Reading Room</title>
        <meta name="description" content="Browse 350+ curated books across leadership, mindset, career growth, culture, and stories. Search by title, author, category, or theme at The Reading Room." />
        <link rel="canonical" href="https://thereading-room.com/browse" />
        <meta property="og:title" content="Browse Library | The Reading Room" />
        <meta property="og:description" content="Explore 350+ curated books across 5 themes. Find your next great read." />
        <meta property="og:url" content="https://thereading-room.com/browse" />
      </Helmet>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b" style={{ background: 'rgba(255,230,204,0.9)', backdropFilter: 'blur(12px)', borderColor: 'var(--highlight-border)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BookOpen className="w-6 h-6" style={{ color: 'var(--muted)' }} />
            <span className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--text)' }}>
              The Reading Room
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" style={{ color: 'var(--muted)' }}>
                Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--text)' }}>
            {selectedHomepageTheme || "Browse Library"}
          </h1>
          <p className="text-lg" style={{ color: 'var(--muted)' }}>
            {selectedHomepageTheme 
              ? `Showing ${total} books in "${selectedHomepageTheme}"`
              : `Explore ${total} books from the collection`}
          </p>
          {selectedHomepageTheme && (
            <button onClick={clearFilters} className="mt-3 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-white/60" style={{ color: 'var(--muted)', background: 'var(--icon-bg)' }} data-testid="clear-theme-filter">
              <X className="w-3.5 h-3.5" /> Clear theme filter
            </button>
          )}
        </div>

        {/* Search & Filter */}
        <div className="mb-10 space-y-6">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--muted)' }} />
            <Input
              data-testid="search-input"
              placeholder="Search by title or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 rounded-full text-lg"
              style={{ borderColor: 'var(--highlight)', background: 'var(--card-bg)' }}
            />
          </form>

          {/* Categories */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 mr-2" style={{ color: 'var(--muted)' }} />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`category-pill ${selectedCategory === category ? 'active' : ''}`}
                data-testid={`category-${category}`}
              >
                {category}
              </button>
            ))}
            {(selectedCategory !== "All" || searchQuery || selectedHomepageTheme) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm rounded-full transition-colors"
                style={{ color: 'var(--muted)' }}
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Books Grid */}
        {loading ? (
          <div className="book-grid">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[2/3] rounded-lg mb-4" style={{ background: 'var(--skeleton)' }} />
                <div className="h-4 rounded w-3/4 mb-2" style={{ background: 'var(--skeleton)' }} />
                <div className="h-3 rounded w-1/2" style={{ background: 'var(--bg-alt2)' }} />
              </div>
            ))}
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--muted)' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--muted2)' }}>No books found</h3>
            <p style={{ color: 'var(--muted)' }}>Try adjusting your search or filter</p>
          </div>
        ) : (
          <div className="book-grid">
            {books.map((book) => (
              <BookCard key={book.book_id} book={book} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 mt-20" style={{ background: 'var(--hero-dark)', borderTop: '1px solid var(--highlight-border-light)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-sm" style={{ color: 'var(--on-hero-muted)' }}>
            Curated with love by Lopa
          </p>
          <Link to="/admin/login" className="text-sm" style={{ color: 'var(--muted)' }}>
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}

// Book Card Component
const FALLBACK_COLORS = [
  "from-amber-900 to-amber-700",
  "from-stone-800 to-stone-600",
  "from-emerald-900 to-emerald-700",
  "from-yellow-900 to-yellow-700",
  "from-orange-900 to-orange-700",
  "from-teal-900 to-teal-700",
  "from-cyan-900 to-cyan-700",
  "from-lime-900 to-lime-700",
];

const BookCard = ({ book }) => {
  const [imgError, setImgError] = useState(false);
  const isPlaceholder = !book.cover_image || book.cover_image.includes('placehold.co') || book.cover_image.includes('via.placeholder');
  const showFallback = isPlaceholder || imgError;
  const colorIdx = book.title.length % FALLBACK_COLORS.length;

  return (
    <Link 
      to={`/book/${book.book_id}`}
      className="book-card group"
      data-testid={`book-card-${book.book_id}`}
    >
      <div className="book-cover aspect-[2/3] mb-4 overflow-hidden rounded-lg shadow-md group-hover:shadow-xl transition-shadow" style={{ background: 'var(--skeleton)' }}>
        {showFallback ? (
          <div className={`w-full h-full bg-gradient-to-br ${FALLBACK_COLORS[colorIdx]} flex flex-col items-center justify-center p-4 text-center`}>
            <BookOpen className="w-8 h-8 text-white/40 mb-2" />
            <h4 className="text-white font-bold text-sm leading-tight line-clamp-3 mb-1">{book.title}</h4>
            <p className="text-white/70 text-xs">{book.author}</p>
          </div>
        ) : (
          <img
            src={proxyCoverUrl(book.cover_image)}
            alt={`Cover of ${book.title}`}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      <div className="space-y-1">
        <h3 className="font-semibold transition-colors line-clamp-2 text-sm md:text-base" style={{ color: 'var(--text)' }}>
          {book.title}
        </h3>
        <p className="text-sm truncate" style={{ color: 'var(--muted)' }}>{book.author}</p>
        {book.rating && (
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
            <span className="text-sm" style={{ color: 'var(--muted)' }}>{book.rating}</span>
          </div>
        )}
        {book.category && book.category !== "General" && (
          <span className="inline-block px-2 py-0.5 rounded text-xs" style={{ background: 'var(--bg-alt2)', color: '#5C6B3C' }}>
            {book.category}
          </span>
        )}
      </div>
    </Link>
  );
};
